import { Token } from '@kodiak-finance/sdk-core'
import { Apy } from 'components/Apy'
import { ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import { useApproveStep } from 'components/StepLine/common/ApproveStep'
import useStepWithNext from 'components/StepLine/context/hooks/useStepWithNext'
import StepLineModal from 'components/StepLine/StepLineModal'
import { BAULT_ROUTER_ADDRESSES } from 'constants/addresses'
import { useBaultBasicInfo } from 'hooks/useBault'
import { useConnection } from 'hooks/useConnection'
import { useApiTokenValue } from 'hooks/useTokenPrice'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useCallback, useMemo, useState } from 'react'
import { ArrowUpRight } from 'react-feather'
import { Link } from 'react-router-dom'
import { IslandApiResponse } from 'state/data/slice'
import styled from 'styled-components'
import { ThemedText } from 'theme'
import { formatDollarAmount } from 'utils/formatDollarAmt'

import { StakingData } from '../../config'
import { useOnBaultDeposit } from '../../IslandManagementCard/hooks/useOnStakeCallback'
import { useOnVaultUnstakeCallback } from '../../IslandManagementCard/hooks/useOnUnstakeCallback'
import BaultDepositStep from '../../IslandManagementCard/steps/BaultDepositStep'
import WithdrawFromRewardVaultStep from '../../IslandManagementCard/steps/WithdrawFromRewardVaultStep'
import { StakeInterface } from '..'
import MigrateToAutoCompoundModal from '../modals/MigrateToAutoCompoundModal'

const Banner = styled(LightCard)`
  padding: 20px;
`

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;
`

const BadgePill = styled.div`
  background: ${({ theme }) => theme.cardPrimary};
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 100px;
  text-transform: uppercase;
  display: flex;
  gap: 8px;
  align-items: center;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
`

const TitleSection = styled.div`
  margin-bottom: 4px;
`

const Title = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.textPrimary};
  margin: 0;
  line-height: 1.2;
`

const Description = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 14px;
  line-height: 1.5;
  max-width: 580px;
`

const ActionColumn = styled.div`
  display: flex;
  gap: 12px;

  flex-direction: column;
`

const LearnMore = styled.div`
  padding: 8px 12px;
  display: flex;
  align-items: center;
  max-width: 120px;
  gap: 4px;
  border-radius: 4px;
  // background: ${({ theme }) => theme.buttonPrimary};

  font-size: 14px;
`

interface AutoCompoundInfoBannerProps {
  stakingType?: StakingData['stakingType']
  stakedAmount?: StakeInterface[] | undefined
  rewardVault?: string
  baultAddress?: string
  apy?: string
  pool?: IslandApiResponse | null
}

export default function AutoCompoundInfoBanner({
  stakingType = 'rewardVault',
  stakedAmount,
  rewardVault,
  baultAddress,
  apy,
  pool,
}: AutoCompoundInfoBannerProps) {
  const [showMigrationModal, setShowMigrationModal] = useState(false)
  const [showStepLineModal, setShowStepLineModal] = useState(false)
  const [migrationAmount, setMigrationAmount] = useState('')
  const [migrationLPCurrency, setMigrationLPCurrency] = useState<Token | undefined>()

  const { value: stakedAmountUSD } = useApiTokenValue(stakedAmount?.[0].liquidity)
  const { account, chainId } = useConnection()
  const baultRouterAddress = BAULT_ROUTER_ADDRESSES[chainId]

  const { data } = useBaultBasicInfo(baultAddress)

  // const lpCurrency = stakedAmount?.[0]?.liquidity?.currency
  const typedMigrationAmount = useMemo(() => {
    return tryParseCurrencyAmount(migrationAmount, migrationLPCurrency)
  }, [migrationAmount, migrationLPCurrency])

  const {
    onConfirmWithdrawStakes: onConfirmVaultWithdraw,
    attemptingTxn: attemptingRewardVaultTxn,
    hash: rewardVaultHash,
    clear: clearVault,
  } = useOnVaultUnstakeCallback(typedMigrationAmount, rewardVault)

  const {
    onStake: onBaultDeposit,
    attemptingTxnStake: attemptingTxnBaultDeposit,
    txHashStake: txHashBaultDeposit,
    clear: clearBaultDeposit,
  } = useOnBaultDeposit(typedMigrationAmount, baultRouterAddress, baultAddress, account)

  const handleOpenMigrationModal = useCallback(() => setShowMigrationModal(true), [setShowMigrationModal])
  const handleCloseMigrationModal = useCallback(() => setShowMigrationModal(false), [setShowMigrationModal])

  const handleStartMigration = useCallback(
    (amount: string) => {
      setMigrationLPCurrency(stakedAmount?.[0]?.liquidity?.currency)
      setMigrationAmount(amount)
      setShowMigrationModal(false)
      setShowStepLineModal(true)
    },
    [stakedAmount]
  )

  const handleCloseStepModal = useCallback(() => {
    setShowStepLineModal(false)
    clearVault()
    clearBaultDeposit()
    setMigrationAmount('')
    setMigrationLPCurrency(undefined)
  }, [clearBaultDeposit, clearVault])

  const hasStakedAmount = useMemo(
    () => stakedAmount && stakedAmount[0]?.liquidity && !stakedAmount[0].liquidity.equalTo('0'),
    [stakedAmount]
  )

  const showMigrationOption = useMemo(
    () =>
      stakingType === 'rewardVault' && hasStakedAmount && rewardVault && baultAddress && rewardVault && baultAddress,
    [stakingType, hasStakedAmount, rewardVault, baultAddress, rewardVault, baultAddress]
  )

  const withdrawStep = useStepWithNext('withdrawStep', WithdrawFromRewardVaultStep, {
    amount: typedMigrationAmount,
    rewardVault,
    pool: pool ?? null,
    onWithdraw: onConfirmVaultWithdraw,
    hash: rewardVaultHash,
    attemptingTxn: attemptingRewardVaultTxn,
    pendingText: 'Unstaking from Reward Vault...',
    autoConfirm: true,
    transactionWaiting: true,
  })

  const approveStep = useApproveStep(typedMigrationAmount, baultRouterAddress)

  const depositStep = useStepWithNext('depositStep', BaultDepositStep, {
    amount: typedMigrationAmount,
    onStake: onBaultDeposit,
    baultAddress,
    hash: txHashBaultDeposit,
    attemptingTxn: attemptingTxnBaultDeposit,
    pendingText: 'Depositing into Bault...',
  })

  const migrationSteps = [withdrawStep, approveStep, depositStep]
  const anyTransactionInProgress = attemptingRewardVaultTxn || attemptingTxnBaultDeposit

  return (
    <>
      <Banner>
        <TopRow>
          <BadgePill>AUTO-COMPOUND (Baults)</BadgePill>
          <Apy apy={data?.apy} />
        </TopRow>

        <Content>
          <TitleSection>
            <Title>
              {showMigrationOption ? 'Boost Your Yields with Auto-Compounding' : 'Auto-Compounding Available'}
            </Title>
          </TitleSection>

          <Description>
            {showMigrationOption
              ? `You have ${formatDollarAmount(
                  stakedAmountUSD
                )} staked in the Reward Vault. Migrate to baults to automatically reinvest your rewards and maximize your yield.`
              : 'This pool supports auto-compounding, which automatically reinvests your rewards to grow your position over time without manual intervention.'}
          </Description>
        </Content>

        <ActionColumn>
          {showMigrationOption && (
            <ButtonPrimary onClick={handleOpenMigrationModal} disabled={anyTransactionInProgress}>
              Migrate to Auto-Compound
            </ButtonPrimary>
          )}
          <Link to="https://documentation.kodiak.finance/protocol/baults" target="_blank" style={{ color: 'white' }}>
            <LearnMore>
              Learn More
              <ArrowUpRight size="18px" />
            </LearnMore>
          </Link>
        </ActionColumn>
      </Banner>

      {/* Migration Modals */}
      {/* {showMigrationOption && ( */}
      <>
        <MigrateToAutoCompoundModal
          isOpen={showMigrationModal}
          onDismiss={handleCloseMigrationModal}
          onConfirm={handleStartMigration}
          stakedAmount={stakedAmount?.[0]?.liquidity}
          // estimatedApr={apy}
        />

        <StepLineModal
          isOpen={showStepLineModal}
          onDismiss={handleCloseStepModal}
          steps={migrationSteps}
          header={<ThemedText.DeprecatedMain>Migrate to Auto-Compound</ThemedText.DeprecatedMain>}
          onFinish={handleCloseStepModal}
        />
      </>
      {/* )} */}
    </>
  )
}
